import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import {
  BrokerPortfolioStatusesEnum,
  BrokerPortfolioTypesEnum,
} from "dash-common";
import {
  Flex,
  Form,
  Select,
  Input,
  Button,
  useFormValidation,
  FormErrorTypes,
  Text,
  Icon,
  useServerResponseTranslation,
  toast,
  Box,
  Toggle,
  FormContext,
  ViewerContext,
} from "dash-ui";
import {
  useChangePortfolioStatusMutation,
  useUpdatePortfolioPasswordMutation,
  useGetPortfolioPasswordLazyQuery,
  useUpdateShowPopupOnPortfolioMutation,
  useGetPortfolioFolderByIdQuery,
} from "gql/generated.graphql";
import {
  getPreviewPortfolioLink,
  getPreviewPortfolioPropertyLink,
} from "common/helpers/portfolio";
import {
  ShareFormFields,
  ShareFormFieldsList,
} from "containers/portfolio/SharePortfolioModal/SharePortfolioModal.constants";

import { ToggleWrapper } from "./SharePortfolioForm.styles";
import { ApolloError } from "@apollo/client";

const AlertStyle = styled(Flex)`
  border-radius: ${({ theme }) => 2 * theme.space}px;
  background-color: ${({ theme }) => theme.colors.n20};
`;

function SharePortfolioForm({
  portfolioId,
  onCancel,
  isEdit,
}: {
  portfolioId?: any;
  onCancel: () => void;
  isEdit?: boolean;
}) {
  const [showPopup, setShowPopup] = useState<Boolean | null | undefined>(false);
  const { t } = useTranslation(["forms", "common", "pages"]);
  const [formErrors, , [setShareTypeError, , setPasswordError]] =
    useFormValidation(ShareFormFieldsList);
  const translateErrorMessage = useServerResponseTranslation();
  const [isMediaKit, setIsMediaKit] = useState(false);
  const theme = useTheme();
  const { formState, stateHandlers, formOnSubmit, formOnError, formOnSuccess } =
    useContext(FormContext);
  const { broker, profile } = useContext(ViewerContext);

  const [changePortfolioStatus, { loading: changeStatusLoading }] =
    useChangePortfolioStatusMutation();
  const [updatePortfolioPassword, { loading: passwordUpdateLoading }] =
    useUpdatePortfolioPasswordMutation();
  const [
    getPortfolioPasswordQuery,
    { loading: getPortfolioPasswordQueryLoading },
  ] = useGetPortfolioPasswordLazyQuery();
  const [updateShowPopupOnPortfolio, { loading: updatePopupLoading }] =
    useUpdateShowPopupOnPortfolioMutation();
  const {
    data,
    loading: portfolioLoading,
    refetch,
  } = useGetPortfolioFolderByIdQuery({
    variables: {
      portfolioFolderId: portfolioId,
    },
  });

  const isPassword = formState.shareType === "password";
  const loading =
    changeStatusLoading ||
    passwordUpdateLoading ||
    portfolioLoading ||
    updatePopupLoading;
  const portfolio = data?.getPortfolioFolderById;

  const validPassword = (value: string) => {
    if (!value) {
      setPasswordError({
        type: FormErrorTypes.Info,
        message: t`messages.passwordRequired`,
      });
      return false;
    }
    setPasswordError(null);
    return true;
  };
  const onPasswordTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    stateHandlers[ShareFormFields.Password](value);
    validPassword(value);
  };

  const onChangeShowPopup = async () => {
    setShowPopup((value) => !value);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();

    try {
      // 3 mutations for essentially a portfolio update...
      // should wait for re-schema to better organize this
      await updatePortfolioPassword({
        variables: {
          portfolioId: portfolio?._id,
          password: isPassword ? formState.password : "",
        },
      });

      await changePortfolioStatus({
        variables: {
          portfolioId: portfolio?._id,
          isAddedMediaKit: isMediaKit,
          status: isPassword
            ? BrokerPortfolioStatusesEnum.PUBLIC
            : formState.shareType,
        },
      });

      await updateShowPopupOnPortfolio({
        variables: {
          portfolioId: portfolio?._id,
          showPopup: !!showPopup,
        },
      });

      formOnSuccess();
      formOnError();

      const link = portfolio
        ? portfolio?.type === BrokerPortfolioTypesEnum.ITEM
          ? getPreviewPortfolioPropertyLink(portfolio, broker, profile)
          : getPreviewPortfolioLink(portfolio, broker, profile)
        : "";

      stateHandlers[ShareFormFields.Link](link);

      navigator.clipboard.writeText(link);
      toast.success(t("pages:portfoliosPage:toasts:shareLinkCopied"));
      refetch();

      onCancel();
    } catch (error) {
      if (error instanceof ApolloError) {
        formOnError(
          translateErrorMessage(error, ["common:brokerPortalServerErrors"])
        );
      }
      setShareTypeError({ type: FormErrorTypes.Invalid, message: "" });
    }
  };

  const onEditSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();
  };

  // set default value effect
  useEffect(() => {
    if (portfolio?.type === BrokerPortfolioTypesEnum.ITEM) {
      stateHandlers.currentPortfolioId(portfolio?._id);
    }

    setIsMediaKit(!!portfolio?.isAddedMediaKit);

    const newStatus = portfolio?.withPassword ? "password" : portfolio?.status;

    stateHandlers[ShareFormFields.ShareType](newStatus);

    if (newStatus) {
      const newLink = portfolio
        ? portfolio?.type === BrokerPortfolioTypesEnum.ITEM
          ? getPreviewPortfolioPropertyLink(portfolio, broker, profile)
          : getPreviewPortfolioLink(portfolio, broker, profile)
        : "";

      stateHandlers[ShareFormFields.Link](newLink);
    }

    if (portfolio?.withPassword) {
      getPortfolioPasswordQuery({
        variables: { portfolioId: portfolio?._id },
        fetchPolicy: "network-only",
      }).then(({ data }) => {
        const { getPortfolioPassword: password } = data || {};
        if (password) {
          stateHandlers[ShareFormFields.Password](password);
        }
      });
    }

    setShowPopup(portfolio?.showPopup);

    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio]);

  return (
    <Form id="share-portfolio-form" className="share-portfolio-form" onSubmit={isEdit ? onEditSubmit : onSubmit}>
      {!isEdit && (
        <>
          <Form.Group mt={1}>
            <Form.Label>{t`labels.share`}</Form.Label>
            <Flex flexDirection="column" gap={16}>
              <Select
                // !!!important select element overwrite children when opens,
                // it breaks useOutsideClick logic and causes closing of dropdown when we use select inside dropdown
                // to fix this we prevent useOutsideClick call when click on Select
                onMouseDown={(e) => e.stopPropagation()}
                withCheckbox={false}
                leftIcon={null}
                isSearchable={false}
                w="100%"
                mr={2}
                value={formState.shareType}
                options={[
                  {
                    value: BrokerPortfolioStatusesEnum.PRIVATE,
                    label: t(
                      "pages:portfolioDetailsPage:sharePortfolioForm:privateShareType"
                    ),
                  },
                  {
                    value: BrokerPortfolioStatusesEnum.PUBLIC,
                    label: t(
                      "pages:portfolioDetailsPage:sharePortfolioForm:publicShareType"
                    ),
                  },
                  {
                    value: "password",
                    label: t(
                      "pages:portfolioDetailsPage:sharePortfolioForm:passwordType"
                    ),
                  },
                ]}
                onChanged={(value) =>
                  stateHandlers[ShareFormFields.ShareType](value)
                }
                $errorType={formErrors[ShareFormFields.ShareType]?.type}
              />
              <Flex
                alignItems="center"
                justifyContent="space-between"
                w="100%"
                gap={16}
                flexWrap="wrap">
                <ToggleWrapper px={3} py={2} flex="1">
                  <Toggle
                    // @ts-ignore
                    checked={isMediaKit}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      if (!event.target.checked) {
                        stateHandlers[ShareFormFields.AddedMedia]([
                          {
                            portfolioId: formState.currentPortfolioId,
                            mediaIds: [],
                          },
                        ]);
                      }
                      setIsMediaKit(event.target.checked);
                    }}
                  />
                  <Box ml={3}>
                    <Text whiteSpace="nowrap" color={theme.colors.n80}>
                      {t("pages:mediaKit.dashDownloads")}
                    </Text>
                  </Box>
                </ToggleWrapper>

                <ToggleWrapper px={3} py={2} flex="1">
                  <Toggle
                    // @ts-ignore
                    checked={showPopup}
                    onChange={onChangeShowPopup}
                  />
                  <Box ml={3}>
                    <Text whiteSpace="nowrap" color={theme.colors.n80}>
                      Show Agent Popup
                    </Text>
                  </Box>
                </ToggleWrapper>
              </Flex>
            </Flex>
            <Form.Feedback
              $errorType={formErrors[ShareFormFields.ShareType]?.type}>
              {formErrors[ShareFormFields.ShareType]?.message}
            </Form.Feedback>
          </Form.Group>
          {(formState.shareType === BrokerPortfolioStatusesEnum.PUBLIC ||
            isPassword) &&
            formState.link && (
              <Flex mt={3} w="100%" alignItems="center">
                <Form.Group w="100%">
                  <Form.Label>{t`labels.link`}</Form.Label>
                  <Input
                    w="100%"
                    value={formState.link}
                    onChange={(e) => e.preventDefault()}
                  />
                </Form.Group>
                <Box mt={5} style={{ cursor: "pointer" }}>
                  <Icon
                    name="contentCopy"
                    size={24}
                    color={theme.colors.b50}
                    ml={3}
                    onClick={() => {
                      navigator.clipboard.writeText(formState.link);
                      toast.success(
                        t("pages:dashboardPage.currencyConverterWidget.copied")
                      );
                    }}
                  />
                </Box>
              </Flex>
            )}
          {formState.shareType === BrokerPortfolioStatusesEnum.PRIVATE && (
            <AlertStyle mt={3} px={2} py={2}>
              <Icon name="lock" color={theme.colors.n200} mr={1.5} size={24} />
              <Text color={theme.colors.n80}>
                {t("pages:portfolioDetailsPage:sharePortfolioForm:alertLabel")}
              </Text>
            </AlertStyle>
          )}
          {isPassword && (
            <Form.Group mt={3}>
              <Form.Label>{t`labels.password`}</Form.Label>
              <Form.InputPassword
                disabled={getPortfolioPasswordQueryLoading}
                value={formState.password || ""}
                w="100%"
                placeholder={
                  t(
                    "pages:portfolioDetailsPage:sharePortfolioForm:passwordPlaceholder"
                  ) || ""
                }
                onChange={onPasswordTextChange}
                $errorType={formErrors[ShareFormFields.Password]?.type}
              />
              <Form.Feedback
                $errorType={formErrors[ShareFormFields.Password]?.type}>
                {formErrors[ShareFormFields.Password]?.message}
              </Form.Feedback>
            </Form.Group>
          )}
          <Form.Group mt={3}>
            <Form.Feedback $errorType={FormErrorTypes.Invalid}>
              {formState.errorSlug}
            </Form.Feedback>
          </Form.Group>
        </>
      )}
      <Flex mt={3}>
        <Button mr={2} type="submit" disabled={loading} isLoading={loading}>
          {isEdit ? t("common:labels.save") : t`labels.share`}
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          variation="outline">{t`common:labels:cancel`}</Button>
      </Flex>
    </Form>
  );
}

export default SharePortfolioForm;
